import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { ConfirmationDialogProps } from "./ConfirmationDialog.types";

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  dialogText,
  dialogTitleText,
  discardText,
  agreeText,
  agreeColor,
  agreeIcon,
  showDiscardButton = true,
}: ConfirmationDialogProps) => {
  const { t } = useTranslation();

  const handleAgree = useCallback(() => {
    onClose(true);
  }, [onClose]);

  const handleDisagree = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <Dialog
      data-testid="confirmation-dialog-test-id"
      open={open}
      onClose={handleDisagree}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle tabIndex={-1} sx={{ px: 3, py: 2 }}>
        {dialogTitleText}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 0 }}>
        <DialogContentText variant="body1" sx={{ whiteSpace: "pre-wrap", color: "#000" }} id="alert-dialog-description">
          {dialogText}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          p: 3,
          gap: theme.spacing(2),
          // A lone confirm button stays on the right, where the primary action sits whenever there are two.
          justifyContent: showDiscardButton ? "space-between" : "flex-end",
        })}
      >
        {showDiscardButton && (
          <Button data-testid="dialog-disagree-button" onClick={handleDisagree} variant="outlined">
            {discardText ?? t("common.cancel")}
          </Button>
        )}
        <Button
          data-testid="dialog-agree-button"
          onClick={handleAgree}
          variant="contained"
          color={agreeColor}
          startIcon={agreeIcon}
        >
          {agreeText ?? t("common.ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
